import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { Assignable } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import {
  useEditEvent,
  useRetrieveCurrentPlanzone,
  useRetrieveEvent,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";

import { WorkspaceContext } from "@scenes/workspaceContext";
import { Safeguard } from "@scenes/safeguard";
import { Loader } from "@common/loader";
import { TagList } from "@common/tagList";
import { Modal } from "@common/modal";
import { ElementAttachments } from "@common/elementAttachments";
import { ProjectRedirection } from "@common/projectRedirection";
import { DatesPickers } from "@scenes/event/datesPickers";
import { Comments } from "@scenes/comments";
import { ScenePlaceholder } from "@common/scenePlaceholder";
import { useModal } from "@root/client/modal";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { ProjectElementsSecondaryCriteria } from "@scenes/projectElementsSecondaryCriteria";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Breadcrumbs } from "@common/breadcrumbs";
import { FolderIcon } from "@common/folderIcon";
import { Props } from "./types";
import { Details } from "./details";
import { Timestamp } from "./timestamp";
import { ParticipantsList } from "./participantsList";
import styles from "./styles.scss";

function Event({ eventId, isMultiProject, close }: Props) {
  const { t } = useTranslation();
  const {
    isRequestPending,
    isRequestFailure,
    event,
    hasRetrievedEvent,
    retrieveEvent,
  } = useRetrieveEvent(eventId);
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    event.project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const {
    error,
    isRequestPending: isEditRequestPending,
    editEvent,
  } = useEditEvent(event);

  React.useEffect(() => {
    !hasRetrievedEvent && retrieveEvent(eventId);
  }, [eventId, hasRetrievedEvent]);

  return (
    <WorkspaceContext type="project" project={event.project}>
      <Safeguard
        useCases={[UseCases.editEvent, UseCases.postComment]}
        context={{ event }}
      >
        {({ canEditEvent, canPostComment }) => (
          <div
            className={classNames(
              { [styles.event]: !isRequestPending },
              { [styles.eventLoading]: isRequestPending },
            )}
          >
            {isRequestPending ? (
              <div className={styles.loader}>
                <div className={styles.loaderMain}>
                  <Loader />
                </div>
              </div>
            ) : (
              <Modal
                headerClassName={styles.header}
                closeIconClassName={styles.icon}
                header={
                  <div className={styles.contentHeader}>
                    <h1 className={styles.title}>
                      <Breadcrumbs
                        onNavigate={close}
                        breadcrumbs={[
                          {
                            name: event.project.name,
                            to: `/projects/${event.project.uuid}`,
                            icon: <FolderIcon className={styles.folderIcon} />,
                          },
                          {
                            name: event.list.name,
                            click: () => {
                              close();
                              pushView({
                                key: UseCases.retrieveList,
                                data: { listId: event.list.uuid },
                              });
                            },
                          },
                          { name: event.name },
                        ]}
                      />
                    </h1>

                    <div
                      className={classNames(styles.rightContent, {
                        [styles.rightContentMultiProject]: isMultiProject,
                      })}
                    >
                      {isMultiProject && (
                        <ProjectRedirection
                          className={styles.contentProjectRedirection}
                          projectId={event.project.uuid}
                          close={close}
                        />
                      )}
                      <div>
                        <Timestamp
                          event={event}
                          isMultiProject={isMultiProject}
                        />
                      </div>
                    </div>
                  </div>
                }
                close={close}
              >
                {isRequestFailure ? (
                  <div className={styles.scenePlaceholder}>
                    <ScenePlaceholder goBack={close} />
                  </div>
                ) : (
                  <div className={styles.contentModal}>
                    <div className={styles.main}>
                      <div className={styles.content}>
                        <div className={styles.scrollWrapper}>
                          <div className={styles.details}>
                            <Details
                              event={event}
                              error={error}
                              readOnly={!canEditEvent}
                            />
                          </div>
                          <div className={styles.list}>
                            <ParticipantsList
                              project={event.project}
                              participants={event.participants}
                              select={
                                canEditEvent
                                  ? (resources) =>
                                      editEvent({
                                        event,
                                        nextEvent: {
                                          ...event,
                                          participants: [
                                            ...event.participants,
                                            ...resources,
                                          ],
                                        },
                                      })
                                  : undefined
                              }
                              remove={
                                canEditEvent
                                  ? (resources) =>
                                      editEvent({
                                        event,
                                        nextEvent: {
                                          ...event,
                                          participants:
                                            event.participants.filter(
                                              ({ uuid }) =>
                                                !resources.find(
                                                  (resource) =>
                                                    uuid === resource.uuid,
                                                ),
                                            ),
                                        },
                                      })
                                  : undefined
                              }
                            />
                          </div>
                          <div className={styles.tagList}>
                            <TagList
                              id={event.uuid}
                              tags={event.tags}
                              type={Assignable.EVENT}
                              project={event.project}
                              readOnly={!canEditEvent}
                            />
                          </div>
                          <Safeguard
                            useCases={[
                              UseCases.editProjectElementDetailsConfiguration,
                            ]}
                            context={{ event }}
                          >
                            {({ canEditProjectElementDetailsConfiguration }) =>
                              (template.length > 0 ||
                                canEditProjectElementDetailsConfiguration) && (
                                <div className={styles.secondaryCriteria}>
                                  <div className={styles.row}>
                                    <p className={styles.label}>
                                      {t("retrieveEvent.secondaryCriteria")}
                                    </p>

                                    {canEditProjectElementDetailsConfiguration ? (
                                      <div
                                        className={styles.configurationIconBox}
                                        onClick={() =>
                                          pushView({
                                            key: UseCases.retrieveProjectElementDetailsConfiguration,
                                            data: { project: event.project },
                                          })
                                        }
                                      >
                                        <CogWheelIcon
                                          className={styles.configurationIcon}
                                        />
                                      </div>
                                    ) : (
                                      <div
                                        className={styles.contextualMenuIcon}
                                      />
                                    )}
                                  </div>
                                  <ProjectElementsSecondaryCriteria
                                    project={event.project}
                                    criteriaForms={template.reduce(
                                      (acc, criteria) => ({
                                        ...acc,
                                        [criteria.uuid]: (
                                          <SecondaryCriteriaForm
                                            criteria={criteria}
                                            value={
                                              event.attributes
                                                ? event.attributes[
                                                    criteria.uuid
                                                  ] || ""
                                                : ""
                                            }
                                            flexible
                                            disabled={isEditRequestPending}
                                            submit={(value) =>
                                              editEvent({
                                                event,
                                                nextEvent: {
                                                  ...event,
                                                  attributes: {
                                                    ...event.attributes,
                                                    [criteria.uuid]: value,
                                                  },
                                                },
                                              })
                                            }
                                          />
                                        ),
                                      }),
                                      {},
                                    )}
                                  />
                                </div>
                              )
                            }
                          </Safeguard>
                          <ElementAttachments
                            element={event}
                            project={event.project}
                            elementType="EVENT"
                            readOnly={!canEditEvent}
                          />
                        </div>
                      </div>
                      <div className={styles.timeline}>
                        <div className={styles.timestamp}>
                          <DatesPickers
                            event={event}
                            error={error}
                            readOnly={!canEditEvent}
                          />
                        </div>
                        <div className={styles.comments}>
                          {!event.project.isTemplate && (
                            <Comments
                              subject={event.uuid}
                              type="event"
                              canPost={canPostComment}
                              project={event.project.uuid}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Modal>
            )}
          </div>
        )}
      </Safeguard>
    </WorkspaceContext>
  );
}

export { Event };
